import { parseHumanId } from "@anthra/platform/domain"
import { SwamithraLogo } from "@anthra/ui/brand-icons"
import { Button } from "@anthra/ui/button"
import { buttonVariants } from "@anthra/ui/button-variants"
import { cn } from "@anthra/ui/cn"
import { controlClasses } from "@anthra/ui/control"
import { SkeletonText } from "@anthra/ui/skeleton"
import { Link, useNavigate, useRouterState } from "@tanstack/react-router"
import { type ReactNode, useState } from "react"

import { copy } from "@/copy/not-found-denied"
import { usePanelSession } from "@/features/auth/use-panel-auth"
import { useTheme } from "@/hooks/use-theme"

/**
 * The one designed dead-end (US-01-001.23, US-01-004.18, US-01-005.5).
 *
 * Built against screens/not-found-denied.html — a single component behind
 * every reason a screen refuses to render: a record the id does not resolve to,
 * and a route the signed-in role cannot reach (an Expert opening
 * expert-management). The mark is amber for "you cannot reach this" and blue
 * for "we could not confirm this", never red — none of these is a failure of
 * the platform.
 *
 * `facts` renders as a `<dl>`, matching the design's own fact block; a screen
 * with nothing to state about the attempt passes none.
 *
 * `standalone` carries the design's own topbar/sidenav (`.app`/`.app__topbar`/
 * `.sidenav`) rather than the shared `PanelShell` — it is for the ONE call site
 * with no shell around it at all, the router's own `notFoundComponent`
 * (`routes/__root.tsx`), which renders before the `_panel` layout has any
 * session to gate on. Every other call site already sits inside `PanelShell`,
 * where a second topbar would be a second one, not a match for the design.
 */
export function NotFoundDenied({
	eyebrow,
	title,
	lede,
	facts,
	actions,
	markTone = "warning",
	isLoading = false,
	standalone = false,
}: {
	eyebrow?: string
	title: string
	lede: string
	facts?: ReactNode
	actions: ReactNode
	markTone?: "warning" | "info"
	isLoading?: boolean
	standalone?: boolean
}) {
	if (isLoading) {
		return (
			<Frame standalone={standalone}>
				<div
					className="flex flex-col items-center gap-3 py-10 text-center"
					role="status"
					aria-label="Loading"
				>
					<SkeletonText lines={8} />
				</div>
			</Frame>
		)
	}

	return (
		<Frame standalone={standalone}>
			<div className="flex flex-col items-center gap-3 py-10 text-center">
				{/* The circular mark. `deadend__mark--info` / `--warning` were
				    rules in not-found-denied.css, which this rebuild deleted, so
				    the icon had no ring, no ground and no ink of its own. */}
				<span
					className={cn(
						"rounded-circle flex size-12 shrink-0 items-center justify-center border",
						markTone === "info"
							? "border-border bg-subtle text-subtle-foreground"
							: "border-status-breach-border bg-status-breach-bg text-status-breach-fg",
					)}
					aria-hidden="true"
				>
					<svg
						viewBox="0 0 24 24"
						width="24"
						height="24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<circle
							cx="12"
							cy="12"
							r="9"
						/>
						<path d="M12 8v5" />
						<path d="M12 16h.01" />
					</svg>
				</span>
				{eyebrow ? <p className="text-meta text-muted">{eyebrow}</p> : null}
				<h1 className="text-card-title text-foreground">{title}</h1>
				<p className="text-description text-subtle-foreground">{lede}</p>
				{facts ? <dl className="flex flex-col gap-2">{facts}</dl> : null}
				<div className="flex flex-wrap gap-2">{actions}</div>

				<RecordLookup />

				{/* Why one message for both "missing" and "not yours" — the same
				    explanation regardless of which reason applies, so it is written
				    once here rather than per call site (US-00-006.19). */}
				<details className="text-description text-subtle-foreground">
					<summary>{copy.default.why}</summary>
					<p>{copy.default.why2}</p>
				</details>
			</div>
		</Frame>
	)
}

/**
 * "Looking for something you raised?" (screens/not-found-denied.html's own
 * `.lookup`) — a reference typed here (`#4182`, `V12`, with or without the
 * leading `#`) goes straight to that query or vetting request's own route,
 * which already carries this exact dead-end for an id that turns out to be
 * missing or someone else's (US-01-001.23, US-01-004.18). No record is fetched
 * here to preview it — parsing the reference is the only check this form makes,
 * so a mistyped one costs a field error, never a request for data outside the
 * viewer's own reach.
 */
function RecordLookup() {
	const navigate = useNavigate()
	const [reference, setReference] = useState("")
	const [error, setError] = useState<string | undefined>(undefined)

	return (
		<div className="rounded-control border-status-answered-border bg-status-answered-bg flex items-center gap-2 border px-3 py-2">
			<h3 className="text-card-title text-foreground">{copy.default.hint}</h3>
			<p className="text-meta text-muted">{copy.default.field}</p>
			<form
				className="flex flex-col gap-1.5"
				noValidate
				onSubmit={event => {
					event.preventDefault()
					const parsed = parseHumanId(reference)
					if (parsed === null) {
						setError(copy.error.fieldHelp)
						return
					}
					setError(undefined)
					if (parsed.kind === "vetting") {
						void navigate({
							to: "/vetting/$vettingId",
							params: { vettingId: String(parsed.sequence) },
						})
						return
					}
					void navigate({
						to: "/queries/$queryId",
						params: { queryId: String(parsed.sequence) },
					})
				}}
			>
				<label htmlFor="lookup-reference">{copy.default.fieldRow}</label>
				<div className="flex flex-wrap items-center gap-2">
					<input
						className={cn(controlClasses, "h-11")}
						id="lookup-reference"
						name="ref"
						type="text"
						inputMode="numeric"
						placeholder="#4182 or #V12"
						value={reference}
						aria-invalid={error !== undefined}
						aria-describedby={
							error !== undefined ? "lookup-reference-msg" : "lookup-reference-help"
						}
						onChange={event => {
							setReference(event.target.value)
						}}
					/>
					<button
						className={cn(buttonVariants({ variant: "secondary" }))}
						type="submit"
					>
						{copy.default.btn2}
					</button>
				</div>
				{error !== undefined ? (
					<p
						className="text-description text-subtle-foreground"
						id="lookup-reference-msg"
						role="alert"
					>
						{error}
					</p>
				) : null}
				<p
					className="text-description text-subtle-foreground"
					id="lookup-reference-help"
				>
					{copy.default.fieldHelp}
				</p>
			</form>
		</div>
	)
}

const STANDALONE_NAV = [
	{ to: "/", label: "Queries" },
	{ to: "/vetting", label: "Vetting" },
	{ to: "/reporting", label: "Reporting" },
] as const

/**
 * The design's own `.app` shell — topbar, sidenav and the identity block —
 * for the one route this component reaches with no session established yet.
 * `usePanelSession` is read, not required: a signed-out visitor still gets a
 * dead end (a route the router itself could not resolve), just without a name
 * in the topbar.
 */
function Frame({ standalone, children }: { standalone: boolean; children: ReactNode }) {
	// The router-reading hooks live in their own component, called only for
	// `standalone`, so every other call site — rendered inside `PanelShell`,
	// sometimes with no router in the test harness at all — never calls a hook
	// that needs one (rules-of-hooks: a different component, not a branch inside
	// this one).
	if (!standalone) {
		return (
			<div
				className="flex flex-col gap-6"
				data-screen="not-found-denied"
			>
				{children}
			</div>
		)
	}

	return <StandaloneFrame>{children}</StandaloneFrame>
}

function StandaloneFrame({ children }: { children: ReactNode }) {
	const pathname = useRouterState({ select: state => state.location.pathname })
	const session = usePanelSession()
	const [theme, toggleTheme] = useTheme()
	const themeLabel = theme === "dark" ? "Switch to light theme" : "Switch to dark theme"

	return (
		<div
			className="bg-canvas text-foreground min-h-dvh"
			data-screen="not-found-denied"
		>
			<header className="contents">
				<Link
					className="flex min-w-0 items-center gap-2"
					to="/"
				>
					<SwamithraLogo
						src={`${import.meta.env.BASE_URL}images/swamithra-logo.png`}
						darkSrc={`${import.meta.env.BASE_URL}images/swamithra-logo-dark.png`}
						label="Expert Portal"
					/>
				</Link>
				<div className="ml-auto" />
				{session.data ? (
					<div className="flex min-w-0 flex-col">
						<span
							className="bg-avatar-bg text-avatar-text rounded-circle flex size-9 shrink-0 items-center justify-center font-semibold"
							aria-hidden="true"
						>
							{initials(session.data.fullName)}
						</span>
						<span>
							<span className="text-field-label text-foreground">
								{session.data.fullName}
							</span>
							<span className="text-meta text-muted">
								{session.data.role === "super_admin" ? "Super Admin" : "Expert"}
							</span>
						</span>
					</div>
				) : null}
				<Button
					type="button"
					variant="quiet"
					size="icon"
					aria-label={themeLabel}
					onClick={toggleTheme}
				>
					<svg
						className="size-5"
						viewBox="0 0 20 20"
						width="18"
						height="18"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						aria-hidden="true"
					>
						<circle
							cx="10"
							cy="10"
							r="3.5"
						/>
					</svg>
					<svg
						className="size-5"
						viewBox="0 0 20 20"
						width="18"
						height="18"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						aria-hidden="true"
					>
						<path d="M16 11.5A6.5 6.5 0 0 1 8.5 4a6.5 6.5 0 1 0 7.5 7.5z" />
					</svg>
				</Button>
			</header>

			<div className="flex min-w-0 flex-col gap-1">
				{session.data ? (
					<nav
						className="border-border bg-surface z-header fixed inset-y-0 left-0 hidden w-[var(--width-sidebar)] flex-col gap-6 border-r p-4 lg:flex"
						aria-label="Main"
					>
						<p className="text-overline text-muted uppercase">Panel</p>
						{STANDALONE_NAV.map(item => (
							<Link
								key={item.to}
								className="rounded-control text-description text-subtle-foreground hover:bg-subtle hover:text-foreground focus-visible:ring-ring aria-[current=page]:bg-primary aria-[current=page]:text-primary-foreground flex items-center gap-3 px-3 py-2.5 font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none"
								to={item.to}
								aria-current={pathname === item.to ? "page" : undefined}
							>
								{item.label}
							</Link>
						))}
						<p className="border-border flex flex-wrap items-center gap-2 border-t pt-3">
							Signed in as {session.data.fullName}.
						</p>
					</nav>
				) : null}

				<div className="mx-auto w-full max-w-[var(--width-page)] px-4 py-6 md:px-6 lg:ml-[var(--width-sidebar)] lg:px-8">
					<div className="flex flex-col gap-6">{children}</div>
				</div>
			</div>
		</div>
	)
}

/** First and last initial — "Asha Rao" is AR, a single name is one letter. */
function initials(fullName: string): string {
	const parts = fullName.trim().split(/\s+/).filter(Boolean)
	const first = parts.at(0) ?? ""
	const last = parts.length > 1 ? (parts.at(-1) ?? "") : ""
	return (first.slice(0, 1) + last.slice(0, 1)).toUpperCase()
}
